import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Plus, Calendar, DollarSign, TrendingUp, Sliders, BarChart3, Layers, Trash2, Mail, Send, MapPin, Brain, User, HeartPulse, Building2, Search } from 'lucide-react';
import { OnboardingDisclaimer, Footer } from '../components/LegalDisclaimers';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { RendPlexLogo } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import UserMenu from '../components/UserMenu';
import DashboardHero from '../components/portfolio/DashboardHero';
import AdvisorChipInsights from '../components/admin/AdvisorChipInsights';
import AdvisorTourFunnel from '../components/admin/AdvisorTourFunnel';
import WelcomeTutorialCard from '../components/tutorial/WelcomeTutorialCard';
import TutorialLauncher from '../components/tutorial/TutorialLauncher';
import HelpMenu from '../components/tutorial/HelpMenu';
import { useIsDealScannerEnabled } from '../context/FeatureFlagsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const dealScannerEnabled = useIsDealScannerEnabled();
  const [user, setUser] = useState(location.state?.user || null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [sortBy, setSortBy] = useState('newest');
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [digestLoading, setDigestLoading] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [mapsUsage, setMapsUsage] = useState(null);
  const [intelStats, setIntelStats] = useState(null);
  const [aiAnalysisStats, setAiAnalysisStats] = useState(null);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      const hasSeenOnboarding = localStorage.getItem('rendplex_onboarding_seen');
      if (!hasSeenOnboarding) {
        setShowOnboarding(true);
        localStorage.setItem('rendplex_onboarding_seen', 'true');
      }
      loadDeals();
      return;
    }
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
        loadDeals();
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  const loadDeals = async () => {
    try {
      const params = new URLSearchParams();
      if (sortBy) params.append('sort_by', sortBy);
      const response = await axios.get(`${BACKEND_URL}/api/deals?${params.toString()}`, { withCredentials: true });
      setDeals(response.data);
    } catch (error) {
      console.error('Failed to load deals:', error);
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedComparisons = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/comparisons`, { withCredentials: true });
      setSavedComparisons(response.data);
    } catch { /* Free users get 403 */ }
  };

  const loadPreferences = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/preferences`, { withCredentials: true });
      setDigestEnabled(response.data.email_digest_enabled);
    } catch { /* ignore */ }
  };

  const loadMapsUsage = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/maps-usage`, { withCredentials: true });
      setMapsUsage(response.data);
    } catch { /* Free users get 403 */ }
  };

  const loadIntelStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/intelligence-stats`, { withCredentials: true });
      setIntelStats(response.data);
    } catch { /* Free users get 403 */ }
  };

  const loadAiAnalysisStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/ai-analysis-stats`, { withCredentials: true });
      setAiAnalysisStats(response.data);
    } catch { /* Free users get 403 */ }
  };

  const toggleDigest = async () => {
    const newValue = !digestEnabled;
    setDigestLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/user/preferences`, { email_digest_enabled: newValue }, { withCredentials: true });
      setDigestEnabled(newValue);
      toast.success(newValue ? t('dashboard.digestEnabled') : t('dashboard.digestDisabled'));
    } catch {
      toast.error(t('errors.saveFailed'));
    } finally {
      setDigestLoading(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      await axios.post(`${BACKEND_URL}/api/digest/send`, {}, { withCredentials: true });
      toast.success(t('dashboard.digestSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('dashboard.digestFailed'));
    } finally {
      setSendingDigest(false);
    }
  };

  const deleteSavedComparison = async (comparisonId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/comparisons/${comparisonId}`, { withCredentials: true });
      setSavedComparisons(prev => prev.filter(c => c.comparison_id !== comparisonId));
      toast.success(t('dashboard.comparisonDeleted'));
    } catch {
      toast.error(t('dashboard.comparisonDeleteFailed'));
    }
  };

  useEffect(() => {
    if (user) {
      loadDeals();
      if (user.subscription_plan === 'pro' || user.subscription_plan === 'premium') {
        loadSavedComparisons();
        loadPreferences();
        loadMapsUsage();
        loadIntelStats();
        loadAiAnalysisStats();
      }
    }
  }, [sortBy, user]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      // P1A: wipe temporary Scenario A/B/C slots on logout (legacy path).
      try {
        const mod = await import('../components/scenario/scenarioSessionStorage');
        mod.clearAllScenarioSessions?.();
      } catch { /* silent */ }
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };
  // Retained as a legacy safety net; primary logout flows now live in
  // <UserMenu> (top-right dropdown → Sign Out). Some analytics tests still
  // import this handle via handleLogout.
  // eslint-disable-next-line no-unused-vars
  const _handleLogout = handleLogout;

  const deleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/deals/${dealId}`, { withCredentials: true });
      toast.success('Deal deleted');
      setDeals(deals.filter((d) => d.deal_id !== dealId));
      setSelectedDeals(selectedDeals.filter(id => id !== dealId));
    } catch (error) {
      toast.error('Failed to delete deal');
    }
  };

  const toggleSelectDeal = (dealId) => {
    setSelectedDeals(prev => prev.includes(dealId) ? prev.filter(id => id !== dealId) : [...prev, dealId]);
  };

  const handleCompare = () => {
    if (!isPaid) {
      toast.error(t('dashboard.compareProOnly'));
      navigate('/pricing');
      return;
    }
    if (selectedDeals.length < 2) {
      toast.error(t('dealCompare.selectDeals'));
      return;
    }
    navigate('/compare', { state: { dealIds: selectedDeals } });
  };

  if (isAuthenticated === null || loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#45C04A]"></div>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Wave 1B.2A — gating uses `effective_capabilities` (billing ∪ research
  // premium). Billing-display still reads `subscription_plan` so a Free
  // account with active Research Premium correctly shows "Free" as its
  // real billing tier. See PRE_COHORT_TRACEABILITY_READINESS_AUDIT.md
  // §22.4bis / §22ter.6 / §22ter.7.
  const effectiveCaps = new Set(user?.effective_capabilities || []);
  const hasUnlimitedAnalyses = effectiveCaps.has('unlimited_analyses');
  const researchEntitlement = user?.research_entitlement;
  const researchActive = !!researchEntitlement?.active;
  const researchExpiresAt = researchEntitlement?.expires_at;
  const remainingAnalyses =
    !hasUnlimitedAnalyses && user?.subscription_plan === 'free'
      ? 3 - (user?.analyses_count || 0)
      : null;
  const isPaid = effectiveCaps.has('ai_advisor') || user?.subscription_plan === 'pro' || user?.subscription_plan === 'premium';
  const isPremium = effectiveCaps.has('portfolio_ai') || user?.subscription_plan === 'premium';

  // Bilingual Research Premium chip (minimum participant treatment per
  // §22.15 item 7). Shown only while research entitlement is active.
  const researchChipText = (() => {
    if (!researchActive || !researchExpiresAt) return null;
    try {
      const d = new Date(researchExpiresAt);
      const iso = d.toISOString().slice(0, 10);
      const lang = (user?.language || 'en').toLowerCase();
      return lang === 'fr'
        ? `Recherche Premium — expire le ${iso}`
        : `Research Premium — expires ${iso}`;
    } catch (_e) {
      return null;
    }
  })();

  const getScoreColor = (score) => {
    if (score >= 70) return { bg: 'bg-[#45C04A]/10', text: 'text-[#45C04A]', ring: '#45C04A' };
    if (score >= 40) return { bg: 'bg-amber-500/10', text: 'text-amber-500', ring: '#F59E0B' };
    return { bg: 'bg-red-500/10', text: 'text-red-500', ring: '#EF4444' };
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      {/* Glassmorphism Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 backdrop-saturate-150 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <Link to="/dashboard" className="flex items-center gap-2">
              <RendPlexLogo size="md" />
            </Link>
            <div className="flex items-center gap-3">
              <HelpMenu />
              <DarkModeToggle />
              <LanguageSwitcher variant="minimal" />
              {user?.subscription_plan === 'free' && (
                <Button
                  data-testid="upgrade-button"
                  onClick={() => navigate('/pricing')}
                  className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl px-5 py-2 font-semibold transition-all duration-300 hover:-translate-y-[1px]"
                >
                  {t('dashboard.upgradeToPro')}
                </Button>
              )}
              <UserMenu user={user} />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        {showOnboarding && <OnboardingDisclaimer />}

        {/* First-time welcome tutorial card (self-hides once dismissed/completed). */}
        <WelcomeTutorialCard />

        {/* Portfolio-first hero (Phase 1). Sits ABOVE all existing dashboard content. */}
        <DashboardHero user={user} />

        {/* Header */}
        <div className="mb-10">
          <h1 className="font-primary font-extrabold text-3xl lg:text-4xl text-[#12355B] dark:text-white tracking-tight mb-2">{t('dashboard.title')}</h1>
          <p className="text-slate-500 dark:text-slate-400">{t('dashboard.subtitle')}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#12355B]/5 dark:bg-[#45C04A]/10 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-[#12355B] dark:text-[#45C04A]" />
              </div>
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{t('dashboard.filterAll')}</span>
            </div>
            <p className="text-3xl font-primary font-extrabold text-[#12355B] dark:text-white tracking-tight">{deals.length}</p>
          </div>

          <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#12355B]/5 dark:bg-[#45C04A]/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#12355B] dark:text-[#45C04A]" />
              </div>
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{t('landing.dealScore')}</span>
            </div>
            <p className="text-3xl font-primary font-extrabold text-[#12355B] dark:text-white tracking-tight">
              {deals.length > 0 ? Math.round(deals.reduce((acc, d) => acc + d.deal_score, 0) / deals.length) : '-'}
            </p>
          </div>

          <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#12355B]/5 dark:bg-[#45C04A]/10 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#12355B] dark:text-[#45C04A]" />
              </div>
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Plan</span>
            </div>
            <p className="text-2xl font-primary font-extrabold text-[#12355B] dark:text-white capitalize">{user?.subscription_plan === 'premium' ? t('common.premium') : user?.subscription_plan === 'pro' ? t('common.pro') : t('common.free')}</p>
            {researchChipText && (
              <p
                data-testid="research-premium-chip"
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-[#45C04A]/10 text-[#12355B] dark:text-[#45C04A] border border-[#45C04A]/30"
              >
                {researchChipText}
              </p>
            )}
            {remainingAnalyses !== null && (
              <p className="text-sm text-slate-400 mt-1">{remainingAnalyses} {t('dashboard.usageLimit')}</p>
            )}
          </div>
        </div>

        {/* Weekly Digest Settings (Pro or Premium) */}
        {isPaid && (
          <div data-testid="digest-settings" className="mb-10 bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#12355B]/5 dark:bg-[#45C04A]/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail className="w-5 h-5 text-[#12355B] dark:text-[#45C04A]" />
                </div>
                <div>
                  <h3 className="font-primary font-bold text-[#12355B] dark:text-white text-sm">{t('dashboard.weeklyDigest')}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">{t('dashboard.weeklyDigestDesc')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  data-testid="digest-toggle"
                  onClick={toggleDigest}
                  disabled={digestLoading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    digestEnabled ? 'bg-[#45C04A]' : 'bg-slate-300 dark:bg-slate-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                    digestEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
                <Button
                  data-testid="send-digest-now-button"
                  onClick={sendDigestNow}
                  disabled={sendingDigest}
                  className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#1565C0] dark:hover:bg-[#12355B] text-white rounded-xl px-4 py-2 text-xs font-semibold transition-all duration-300"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {sendingDigest ? t('dashboard.digestSending') : t('dashboard.sendDigestNow')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Property Intelligence Engine (Pro or Premium) */}
        {isPaid && (intelStats || mapsUsage) && (
          <div className="mb-8 bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card" data-testid="intelligence-admin-section">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-gradient-to-br from-[#45C04A] to-[#65D95A] rounded-xl flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">{t('intelligence.adminTitle')}</h3>
                <p className="text-xs text-slate-400">{t('dashboard.mapsUsageSubtitle')}</p>
              </div>
            </div>
            {intelStats && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5 text-center">
                    <p className="text-2xl font-bold text-[#12355B] dark:text-white">{intelStats.total_properties_analyzed}</p>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{t('intelligence.totalAnalyzed')}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5 text-center">
                    <p className="text-2xl font-bold text-[#45C04A]">{intelStats.cache_hit_rate}%</p>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{t('intelligence.cacheHitRate')}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5 text-center">
                    <p className="text-2xl font-bold text-blue-500">{intelStats.average_intelligence_score || 0}</p>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{t('intelligence.avgScore')}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5 text-center">
                    <p className="text-2xl font-bold text-amber-500">{intelStats.total_amenities_cached}</p>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{t('intelligence.amenitiesCached')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
                  <div className="bg-slate-50 dark:bg-slate-800/30 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-[#12355B] dark:text-white">{intelStats.total_api_requests}</p>
                    <p className="text-[9px] text-slate-400">{t('intelligence.totalRequests')}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/30 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-purple-500">{intelStats.google_streetview_calls}</p>
                    <p className="text-[9px] text-slate-400">{t('intelligence.googleCalls')}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/30 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-sky-500">{intelStats.nominatim_calls}</p>
                    <p className="text-[9px] text-slate-400">{t('intelligence.nominatimCalls')}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/30 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-orange-500">{intelStats.overpass_calls}</p>
                    <p className="text-[9px] text-slate-400">{t('intelligence.overpassCalls')}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/30 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-pink-500">{intelStats.ai_calls}</p>
                    <p className="text-[9px] text-slate-400">{t('intelligence.aiCalls')}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/30 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-green-500">{intelStats.fresh_records}</p>
                    <p className="text-[9px] text-slate-400">{t('intelligence.freshRecords')}</p>
                  </div>
                </div>
              </>
            )}
            <div className="flex items-center justify-between bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl p-3">
              <span className="text-sm text-green-700 dark:text-green-400">{t('dashboard.estimatedSavings')}</span>
              <span className="text-lg font-bold text-green-700 dark:text-green-400">${intelStats?.estimated_cost_savings || mapsUsage?.estimated_cost_savings || 0}</span>
            </div>
          </div>
        )}

        {/* AI Decision Engine Analytics (Pro or Premium) */}
        {isPaid && aiAnalysisStats && aiAnalysisStats.total_analyses > 0 && (
          <div className="mb-8 bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card" data-testid="ai-analysis-admin-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">{t('aiEngine.adminTitle')}</h3>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5 text-center">
                <p className="text-2xl font-bold text-[#12355B] dark:text-white">{aiAnalysisStats.total_analyses}</p>
                <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{t('aiEngine.totalAnalyses')}</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5 text-center">
                <p className="text-2xl font-bold text-violet-500">{aiAnalysisStats.average_score}</p>
                <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{t('aiEngine.avgOverall')}</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5 text-center">
                <p className="text-2xl font-bold text-blue-500">{aiAnalysisStats.average_financial}</p>
                <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{t('aiEngine.financialScore')}</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5 text-center">
                <p className="text-2xl font-bold text-green-500">{aiAnalysisStats.average_location}</p>
                <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{t('aiEngine.locationScore')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Advisor Chip Insights — platform-wide ranking of which RendPleX
            feature chips users tap from inside the AI Advisor. Component
            self-gates via 403 for Free users. */}
        {isPaid && <AdvisorChipInsights />}
        {isPaid && (
          <div className="mt-6">
            <AdvisorTourFunnel />
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="new-analysis-button"
              onClick={() => navigate('/analyze')}
              className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('dashboard.newAnalysis')}
            </Button>
            <Button
              data-testid="scenario-analyzer-button"
              onClick={() => navigate('/scenario')}
              className={`rounded-xl px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button ${
                isPaid
                  ? 'bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B]'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
            >
              <Sliders className="w-5 h-5 mr-2" />
              {t('scenario.title')}
              {!isPaid && (
                <span className="ml-2 text-xs bg-[#12355B] text-white px-2 py-0.5 rounded-full font-bold">PRO</span>
              )}
            </Button>
            <Button
              data-testid="compare-properties-dashboard-button"
              onClick={isPaid ? handleCompare : () => navigate('/pricing')}
              className={`rounded-xl px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button ${
                isPaid
                  ? 'bg-[#1565C0] hover:bg-[#12355B] text-white'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              {t('deal.compareProperties')}
              {!isPaid && (
                <span className="ml-2 text-xs bg-gradient-to-r from-[#45C04A] to-[#65D95A] text-[#12355B] px-2 py-0.5 rounded-full font-bold">PRO</span>
              )}
              {isPaid && selectedDeals.length >= 2 && (
                <span className="ml-2 text-xs bg-[#12355B] dark:bg-[#45C04A] text-white dark:text-[#12355B] px-2 py-0.5 rounded-full font-bold">{selectedDeals.length}</span>
              )}
            </Button>
            <Button
              data-testid="investor-profile-button"
              onClick={() => navigate('/profile')}
              className="rounded-xl px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button bg-white dark:bg-[#131824] text-[#12355B] dark:text-white border border-slate-200 dark:border-slate-700 hover:border-[#45C04A]"
            >
              <User className="w-5 h-5 mr-2" />
              {t('investorProfile.navTitle')}
            </Button>
            {dealScannerEnabled && (
              <Button
                data-testid="deal-scanner-button"
                onClick={() => navigate('/deal-scanner')}
                className="rounded-xl px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button bg-gradient-to-r from-[#45C04A] to-[#65D95A] text-[#12355B] hover:from-[#65D95A] hover:to-[#45C04A]"
              >
                <Search className="w-5 h-5 mr-2" />
                {t('dealScanner.navTitle')}
              </Button>
            )}
            {/* My Portfolio + Portfolio Doctor are kept adjacent in this
                order so that when the flex-wrap toolbar breaks to a new
                line the Portfolio button sits directly on top of the
                Doctor button — the two portfolio-scoped entry points
                stay visually grouped. */}
            <Button
              data-testid="portfolio-health-button"
              onClick={() => navigate('/portfolio')}
              className="rounded-xl px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button bg-gradient-to-r from-[#12355B] to-[#1565C0] hover:from-[#1565C0] hover:to-[#12355B] text-white"
            >
              <Building2 className="w-5 h-5 mr-2" />
              {t('myPortfolio.navTitle')}
            </Button>
            <Button
              data-testid="portfolio-doctor-button"
              onClick={isPremium ? () => navigate('/portfolio-doctor') : () => navigate('/pricing')}
              className={`rounded-xl px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button ${
                isPremium
                  ? 'bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'
              }`}
            >
              <HeartPulse className="w-5 h-5 mr-2" />
              {t('portfolioDoctor.navTitle')}
              {!isPremium && (
                <span className="ml-2 text-xs bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#12355B] px-2 py-0.5 rounded-full font-bold">PREMIUM</span>
              )}
            </Button>
            <TutorialLauncher />
          </div>
          
          {deals.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">{t('dashboard.sort')}:</span>
              <select
                data-testid="sort-deals-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-slate-200 dark:border-slate-700 dark:bg-[#131824] dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#45C04A]/30"
              >
                <option value="newest">{`${t('dashboard.sortByDate')} ↓`}</option>
                <option value="oldest">{`${t('dashboard.sortByDate')} ↑`}</option>
                <option value="score_high">{`${t('dashboard.sortByScore')} ↓`}</option>
                <option value="score_low">{`${t('dashboard.sortByScore')} ↑`}</option>
                <option value="cash_flow_high">{`${t('dashboard.sortByCashFlow')} ↓`}</option>
                <option value="cash_flow_low">{`${t('dashboard.sortByCashFlow')} ↑`}</option>
              </select>
            </div>
          )}
        </div>

        {/* Saved Comparisons */}
        {isPaid && savedComparisons.length > 0 && (
          <div className="mb-10" data-testid="saved-comparisons-section">
            <h2 className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#45C04A]" />
              {t('dashboard.savedComparisons')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedComparisons.map((comp) => (
                <div
                  key={comp.comparison_id}
                  data-testid={`saved-comparison-${comp.comparison_id}`}
                  className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:shadow-card-hover transition-all duration-300 cursor-pointer group hover:-translate-y-[1px]"
                  onClick={() => navigate('/compare', { state: { dealIds: comp.deal_ids } })}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-primary font-bold text-[#12355B] dark:text-white truncate">{comp.name}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {comp.deal_ids.length} {t('dashboard.properties')} &middot; {new Date(comp.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      data-testid={`delete-comparison-${comp.comparison_id}`}
                      onClick={(e) => { e.stopPropagation(); deleteSavedComparison(comp.comparison_id); }}
                      className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all ml-2 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deals List */}
        {deals.length === 0 ? (
          <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-16 text-center">
            <BarChart3 className="w-16 h-16 text-slate-200 dark:text-slate-700 mx-auto mb-4" />
            <h3 className="font-primary font-bold text-xl text-[#12355B] dark:text-white mb-2">{t('dashboard.noDeals')}</h3>
            <p className="text-slate-500 dark:text-slate-400 mb-8">{t('dashboard.noDealsDescription')}</p>
            <Button
              data-testid="empty-state-analyze-button"
              onClick={() => navigate('/analyze')}
              className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl px-8 py-3 font-semibold"
            >
              {t('dashboard.analyzeFirstDeal')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deals.map((deal) => {
              const scoreStyle = getScoreColor(deal.deal_score);
              return (
                <div
                  key={deal.deal_id}
                  data-testid={`deal-card-${deal.deal_id}`}
                  className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl hover:shadow-card-hover transition-all duration-300 overflow-hidden group relative hover:-translate-y-[1px]"
                >
                  {isPaid && (
                    <div className="absolute top-4 left-4 z-10">
                      <input
                        type="checkbox"
                        data-testid={`select-deal-${deal.deal_id}`}
                        checked={selectedDeals.includes(deal.deal_id)}
                        onChange={(e) => { e.stopPropagation(); toggleSelectDeal(deal.deal_id); }}
                        className="w-5 h-5 rounded-md border-slate-300 dark:border-slate-600 text-[#45C04A] focus:ring-[#45C04A] cursor-pointer"
                      />
                    </div>
                  )}
                  <div className="p-6 cursor-pointer" onClick={() => navigate(`/deal/${deal.deal_id}`)}>
                    {/* Deal Score Ring */}
                    <div className="flex justify-between items-start mb-5">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                          <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" className="text-slate-100 dark:text-slate-800" strokeWidth="4" />
                          <circle cx="32" cy="32" r="28" fill="none" stroke={scoreStyle.ring} strokeWidth="4" strokeLinecap="round"
                            strokeDasharray={`${(deal.deal_score / 100) * 176} 176`} />
                        </svg>
                        <span className={`absolute inset-0 flex items-center justify-center text-lg font-primary font-extrabold ${scoreStyle.text}`}>
                          {deal.deal_score}
                        </span>
                      </div>
                      <Button
                        data-testid={`delete-deal-${deal.deal_id}`}
                        onClick={(e) => { e.stopPropagation(); deleteDeal(deal.deal_id); }}
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Property Info */}
                    <h3 className="font-primary font-bold text-[#12355B] dark:text-white mb-1 line-clamp-2">
                      {deal.property_address}
                    </h3>
                    <p className="text-slate-400 text-sm mb-5">${deal.purchase_price.toLocaleString()}</p>

                    {/* Metrics */}
                    <div className="space-y-0">
                      <div className="flex justify-between items-center py-2.5 border-t border-slate-100 dark:border-slate-800">
                        <span className="text-sm text-slate-500 dark:text-slate-400">{t('landing.monthlyCashFlow')}</span>
                        <span className={`font-semibold text-sm ${deal.monthly_cash_flow > 0 ? 'text-[#45C04A]' : 'text-red-500'}`}>
                          {deal.monthly_cash_flow > 0 ? '+' : ''}${deal.monthly_cash_flow.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2.5 border-t border-slate-100 dark:border-slate-800">
                        <span className="text-sm text-slate-500 dark:text-slate-400">{t('landing.roi')}</span>
                        <span className="font-semibold text-sm text-[#12355B] dark:text-white">{deal.roi.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between items-center py-2.5 border-t border-slate-100 dark:border-slate-800">
                        <span className="text-sm text-slate-500 dark:text-slate-400">{t('analyze.capRate')}</span>
                        <span className="font-semibold text-sm text-[#12355B] dark:text-white">{deal.cap_rate.toFixed(1)}%</span>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                      <Calendar className="w-3.5 h-3.5 text-slate-300" />
                      <span className="text-xs text-slate-400">{new Date(deal.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
